import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import {
  doKoncaDnia, doPolaDaty, odTeraz, opisLokalny, utcNaWarszawe, walidujOkno, warszawaNaUtc, za24h, zPolaDaty,
  type WynikKonwersji,
} from '@/lib/tresci/czasWarszawa';

/**
 * OD / DO — operator wpisuje czas warszawski, na zewnątrz wychodzi UTC.
 *
 * Godzina nieistniejąca (wiosna) jest odrzucana z wyjaśnieniem. Godzina podwójna (jesień) NIE
 * jest rozstrzygana za operatora — pokazujemy obie i czekamy na wybór. Brak końca to osobna,
 * świadoma decyzja (pole wyboru), nie puste pole przeoczone w pośpiechu.
 */

export interface WartoscHarmonogramu {
  start: Date | null;
  koniec: Date | null;
  bezKonca: boolean;
  /** Okno kompletne i poprawne. */
  ok: boolean;
}

export interface HarmonogramOdDoProps {
  onZmiana: (w: WartoscHarmonogramu) => void;
  wylaczone?: boolean;
  /** Źródło „teraz" — do testów. */
  teraz?: () => Date;
  etykietaGrupy?: string;
}

type Wybor = 'letnia' | 'zimowa' | null;

/** Pole daty → chwila UTC albo opis, dlaczego jej nie ma. */
function rozstrzygnij(wartosc: string, wybor: Wybor): { utc: Date | null; problem: string | null; konwersja: WynikKonwersji | null } {
  if (!wartosc) return { utc: null, problem: null, konwersja: null };
  const lokalny = zPolaDaty(wartosc);
  if (!lokalny) return { utc: null, problem: 'Niepełna data albo godzina.', konwersja: null };
  const k = warszawaNaUtc(lokalny);
  if (k.rodzaj === 'JEDNOZNACZNA') return { utc: k.utc, problem: null, konwersja: k };
  if (k.rodzaj === 'NIEISTNIEJACA') {
    return {
      utc: null,
      problem: `${opisLokalny(lokalny)} nie istnieje w Warszawie — tej nocy zegary przeskakują z 02:00 na 03:00.`,
      konwersja: k,
    };
  }
  if (wybor === null) {
    return { utc: null, problem: `${opisLokalny(lokalny)} występuje tej nocy dwa razy — wybierz, o które chodzi.`, konwersja: k };
  }
  return { utc: wybor === 'letnia' ? k.letnia : k.zimowa, problem: null, konwersja: k };
}

const doPola = (utc: Date): string => doPolaDaty(utcNaWarszawe(utc));

function PoleCzasu({
  etykieta, wartosc, onWartosc, wybor, onWybor, wylaczone,
}: {
  etykieta: string;
  wartosc: string;
  onWartosc: (v: string) => void;
  wybor: Wybor;
  onWybor: (w: Wybor) => void;
  wylaczone: boolean;
}) {
  const { utc, problem, konwersja } = rozstrzygnij(wartosc, wybor);
  return (
    <Box>
      <TextField
        type="datetime-local"
        label={`${etykieta} (czas warszawski)`}
        value={wartosc}
        onChange={(e) => { onWartosc(e.target.value); onWybor(null); }}
        disabled={wylaczone}
        size="small"
        slotProps={{ inputLabel: { shrink: true } }}
        error={problem !== null && konwersja?.rodzaj !== 'DWUZNACZNA'}
        helperText={problem ?? (utc ? `zapis: ${utc.toISOString().slice(0, 16)}Z (UTC)` : ' ')}
      />
      {konwersja?.rodzaj === 'DWUZNACZNA' ? (
        <RadioGroup
          aria-label={`${etykieta} — która godzina`}
          value={wybor ?? ''}
          onChange={(e) => onWybor(e.target.value as Wybor)}
        >
          <FormControlLabel value="letnia" disabled={wylaczone} control={<Radio size="small" />}
            label={`pierwsza — czas letni (UTC+2) = ${konwersja.letnia.toISOString().slice(11, 16)} UTC`} />
          <FormControlLabel value="zimowa" disabled={wylaczone} control={<Radio size="small" />}
            label={`druga — czas zimowy (UTC+1) = ${konwersja.zimowa.toISOString().slice(11, 16)} UTC`} />
        </RadioGroup>
      ) : null}
    </Box>
  );
}

export function HarmonogramOdDo({ onZmiana, wylaczone = false, teraz = () => new Date(), etykietaGrupy = 'Harmonogram' }: HarmonogramOdDoProps) {
  const [startPole, setStartPole] = useState('');
  const [koniecPole, setKoniecPole] = useState('');
  const [wyborStart, setWyborStart] = useState<Wybor>(null);
  const [wyborKoniec, setWyborKoniec] = useState<Wybor>(null);
  const [bezKonca, setBezKonca] = useState(false);

  const start = rozstrzygnij(startPole, wyborStart).utc;
  const koniec = bezKonca ? null : rozstrzygnij(koniecPole, wyborKoniec).utc;
  const okno = useMemo(() => walidujOkno({ start, koniec, bezKonca }), [start, koniec, bezKonca]);

  // Na zewnątrz wychodzi tylko UTC — godziny ścienne zostają w tym komponencie.
  const startMs = start?.getTime() ?? null;
  const koniecMs = koniec?.getTime() ?? null;
  useEffect(() => {
    onZmiana({
      start: startMs === null ? null : new Date(startMs),
      koniec: koniecMs === null ? null : new Date(koniecMs),
      bezKonca,
      ok: okno.ok,
    });
  }, [startMs, koniecMs, bezKonca, okno.ok, onZmiana]);

  const ustawStart = (utc: Date) => { setStartPole(doPola(utc)); setWyborStart(null); };
  const ustawKoniec = (utc: Date) => { setKoniecPole(doPola(utc)); setWyborKoniec(null); setBezKonca(false); };

  return (
    <Box role="group" aria-label={etykietaGrupy}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <PoleCzasu etykieta="Od" wartosc={startPole} onWartosc={setStartPole}
          wybor={wyborStart} onWybor={setWyborStart} wylaczone={wylaczone} />
        <PoleCzasu etykieta="Do" wartosc={bezKonca ? '' : koniecPole} onWartosc={setKoniecPole}
          wybor={wyborKoniec} onWybor={setWyborKoniec} wylaczone={wylaczone || bezKonca} />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Button size="small" variant="outlined" disabled={wylaczone} onClick={() => ustawStart(odTeraz(teraz()))}>
          Od teraz
        </Button>
        <Button size="small" variant="outlined" disabled={wylaczone}
          onClick={() => ustawKoniec(doKoncaDnia(start ?? teraz()))}>
          Do końca dnia
        </Button>
        <Button size="small" variant="outlined" disabled={wylaczone || start === null}
          onClick={() => start && ustawKoniec(za24h(start))}>
          24 godziny
        </Button>
      </Stack>

      <FormControlLabel
        control={<Checkbox size="small" checked={bezKonca} disabled={wylaczone}
          onChange={(e) => setBezKonca(e.target.checked)} />}
        label="Bez daty końcowej — świadomie: treść zostaje do ręcznego wyłączenia"
      />

      {!okno.ok && (startPole || koniecPole || bezKonca) ? (
        <Alert severity="warning" role="alert" sx={{ mt: 1 }}>{okno.powod}</Alert>
      ) : null}
      {okno.ok ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Okno poprawne. Czas w panelu: Europe/Warsaw · zapis: UTC.
        </Typography>
      ) : null}
    </Box>
  );
}
